import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import {
  ApiError,
  InvoicePaymentStatusSchema,
    AuctionItemStatusSchema,
    ReassignModeSchema,
    SaleStateSchema,
    StrandedLotReasonSchema,
  lotKeyOf,
  lotRefOfLine,
} from '@neon-one/auctions-types';
import type {
  ChargeResult,
  DcfCardBrand,
  DcfMethod,
  EventMoney,
  ItemCustodyHistory,
  LotKey,
  LotRef,
  OutstandingOffer,
  PhotoVariant,
  ReassignItemResult,
  ReassignTarget,
    ReassignItemRequest,
    SaleState,
  RefundFailure,
  SettlementBidderSummary,
  SettlementPayload,
  StrandedLot,
  Terminal,
  UninvoicedLot,
  UnbidLot,
  WsClientFrame,
} from '@neon-one/auctions-types';

import { useCurrentAuction, useAdminSession, wasToastReported } from '@/shell';
import { settlementApi } from './api';
import { rollUpByBidder, type BidderRollup } from './bidder-rollup';
import { includeUninvoicedOfferees } from './bidder-companions';
import { consoleMoneyFromRoster } from './console-money';
import { toTargetRow, type TargetRow } from './reassign-target';
import {
  needsWork,
  isResolved,
  invoicePaymentDesignStatus,
  InvoiceStatus,
} from './payment-status';
import type {
  AuditLogEntry,
  CollectionSummary,
  ItemReassignment,
  OfflineMethod,
  PaymentInvoice,
  PaymentLineItem,
  PaymentStatus,
  ReassignCandidate,
  RefundObligationStatus,
  ReminderCadence,
  ReminderChannel,
  ReminderSchedule,
  LoadState,
  SettlementItemLine,
    LotDetail,
    SubmitPlacementArgs,
    PlacementOutcome,
} from './types';

// Obligation states in which money is still on its way back. `succeeded` has
// settled and `void` owed nothing to settle, so neither is pending; `failed`
// is not pending either — it needs a human, and the attention feed is where it
// asks for one. /* §3.1 refund-and-release */
const PENDING_OBLIGATIONS: readonly RefundObligationStatus[] = ['pending', 'submitted'];

// The settlement reality is set by two auction-config fields:
//   paymentMethodRequirement ('required' | 'allow_pay_later') — card to bid
//   autoCheckoutMode         ('auto_charge' | 'offer_default') — charge on close
// They collapse to three valid modes ('no card' + 'auto-charge' is impossible).
// The Console keeps one anatomy and reframes per mode.
export type SettlementMode = 'auto_charge' | 'review_charge' | 'collect';

// Result of a reassign submit. `conflict` is the 409 case (design §edge 5):
// the item moved underneath the caller, true state has been re-read, and the
// caller says so instead of reporting a generic failure.
export interface ReassignOutcome {
  ok: boolean;
  conflict: boolean;
  // What the server actually did, for the surface that has to say so. The
  // result message is built from THIS, never from the pre-flight preview: if
  // the two disagree the response wins, and a surface showing the prediction
  // would be reporting a placement that did not happen (ui-spec §7.1).
  // Null on every failure, and on a replay the server answered from its
  // idempotency record — which is still the outcome that committed.
  result: ReassignItemResult | null;
}

// Why the last settlement action was refused, in the only two facts a surface
// may act on: the API's error CODE, and whether the failure has already been
// toasted by a layer above (the HTTP interceptor speaks for 401/403 wherever
// they happen). The code is what `error-copy.ts` turns into admin-facing words —
// the status alone would not do, since one status covers several genuinely
// different refusals.
//
// The server's own sentence is deliberately absent. It is written for a log
// ("line item is already released") and reading it back to an admin verbatim is
// what this shape exists to make impossible.
export interface ActionError {
  code: string;
  /** Already surfaced to the admin upstream — a surface must not toast again. */
  reported: boolean;
}

// Backend-authoritative add-on aggregates echoed by setDonation /
// setFeeCoverage. Surfaces render these instead of recomputing the
// donor-covered fee client-side.
export interface AddOnTotals {
  subtotalCents: number;
  donationCents: number;
  feeCoverageCents: number;
  totalCents: number;
  taxDeductibleCents: number;
}


// One settlement activity-log entry (audit trail). Prototype: seeded + appended
// in-session; the real persisted log is a backend hookup.
export interface ActivityEntry {
  id: string;
  icon: string;
  text: string;
  actor: string;
  at: string;
}

const ALL_STATUSES: PaymentStatus[] = [
  'paid_auto', 'paid_online', 'paid_offline',
  'awaiting', 'processing', 'failed', 'no_card',
  'partially_refunded', 'refunded',
];

// What the ledger records for a retried obligation. Named once: the reason is
// stored on the refund and read back by finance, so it must not vary by caller.

function newKey(): string {
  return globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `key-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function zeroCounts(): Record<PaymentStatus, number> {
  return ALL_STATUSES.reduce((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {} as Record<PaymentStatus, number>);
}

const SALE = SaleStateSchema.enum;
const MODE = ReassignModeSchema.enum;
const REASON = StrandedLotReasonSchema.enum;
const UNSOLD_STATES: readonly SaleState[] = [SALE.unsold, SALE.needs_buyer, SALE.offer_ended];
const ENDED_OFFER_REASONS: readonly StrandedLot['reason'][] = [
    REASON.offer_declined, REASON.offer_lapsed, REASON.offer_cancelled,
];
type CompanionLot = StrandedLot | UninvoicedLot | UnbidLot;

function lineSaleState(line: PaymentLineItem, invoice: PaymentInvoice): SaleState {
    if (line.saleState) return line.saleState;
    if (line.saleCanceledAt !== null) return SALE.sale_canceled;
    if (line.pendingOffer) return SALE.offer_outstanding;
    if (line.itemStatus === AuctionItemStatusSchema.enum.unsold) return SALE.unsold;
    return line.declined || invoice.status === InvoicePaymentStatusSchema.enum.void ? SALE.needs_buyer : SALE.sold;
}

function lineLotDetail(lotRef: LotRef, { line, invoice }: SettlementItemLine): LotDetail {
    return {
        lotRef, line, invoice, itemTitle: line.itemTitle, lotNumber: line.lotNumber, photo: line.photo,
        fmvCents: line.fmvCents,
        bidStack: line.bidStack, bidCount: line.bidCount, saleState: lineSaleState(line, invoice),
        amountCents: line.amountCents, pendingOffer: line.pendingOffer ?? null,
    };
}

function companionSaleState(lot: CompanionLot): SaleState {
    if (lot.saleState) return lot.saleState;
    if ('winningBidCents' in lot) return SALE.invoicing;
    if ('reason' in lot) return ENDED_OFFER_REASONS.includes(lot.reason) ? SALE.offer_ended : SALE.needs_buyer;
    return SALE.unsold;
}

function companionLotDetail(lotRef: LotRef, lot: CompanionLot, offer: OutstandingOffer | null): LotDetail {
    const amountCents = 'winningBidCents' in lot ? lot.winningBidCents : 'amountCents' in lot ? lot.amountCents : 0;
    const bidStack = lot.bidStack ?? [];
    return {
        lotRef, line: null, invoice: null, itemTitle: lot.itemTitle, lotNumber: lot.lotNumber ?? null,
        photo: lot.photo ?? null, fmvCents: lot.fmvCents ?? null,
        bidStack, bidCount: bidStack.length,
        saleState: companionSaleState(lot), amountCents,
        pendingOffer: offer && { offerPublicId: offer.offerPublicId, bidderUuid: offer.bidderUuid,
            bidderName: offer.bidderName, amountCents: offer.amountCents, expiresAt: offer.expiresAt },
    };
}

function placementFields(args: SubmitPlacementArgs): Omit<ReassignItemRequest, 'lineItemPublicId'> {
    return {
        targetBidderUuid: args.targetBidderUuid, mode: args.mode,
        notify: args.mode === MODE.offer ? true : args.notify,
        ...(args.priceBasis === undefined ? {} : { priceBasis: args.priceBasis }),
        ...(args.priceCents === undefined ? {} : { priceCents: args.priceCents }),
        ...(args.collect === undefined || args.mode === MODE.offer ? {} : { collect: args.collect }),
    };
}

export const useSettlementStore = defineStore('settlement', () => {
  const auctionUuid = ref<string | null>(null);
  const invoices = ref<PaymentInvoice[]>([]);
  const eventMoney = ref<EventMoney | null>(null);
  const bidders = ref<SettlementBidderSummary[]>([]);
  const uninvoicedLots = ref<UninvoicedLot[]>([]);
  const unbidLots = ref<UnbidLot[]>([]);
  // Companion arrays of the same settlement read (api-contracts.md
  // §4.3). They are SERVER answers, not client derivations: which lots are back
  // with the admin, which offers are standing out, and which refund obligations
  // failed after their release committed. Held beside `invoices` because they
  // arrive in the same response and must never be re-read separately — a second
  // read could disagree with the roster they render beside.
  const outstandingOffers = ref<OutstandingOffer[]>([]);
  const strandedLots = ref<StrandedLot[]>([]);
  const refundFailures = ref<RefundFailure[]>([]);
  const loadState = ref<LoadState>('idle');
  // Background re-reads report here, never on loadState. The console's items
  // table and its row controls live inside a `ready`-gated subtree, so putting a
  // refresh failure on loadState blanks the rows and every control on them while
  // the action that triggered the refresh still reports success.
  const refreshState = ref<LoadState>('idle');
  // Generation counter for roster reads — see fetchRoster.
  let rosterRequestId = 0;
  // Whether the first load's `loadState` is still unresolved. The console's
  // first-load gate reads loadState and nothing else, and a superseded read
  // never writes its own terminal state — so whichever read finishes newest has
  // to resolve it, or a load that a background refresh overtakes leaves the page
  // on "Loading settlement…" with no Try again and no path back but a reload.
  let firstLoadPending = false;
  const actionState = ref<LoadState>('idle');
  const lastActionError = ref<ActionError | null>(null);
  // Settlement mode — derived from the auction's config in production.
  // Drives the Console's framing only.
  const mode = ref<SettlementMode>('auto_charge');

  // Activity log (audit trail). Populated from the backend audit log on load;
  // settlement actions append to it in-session. Newest first.
  const activity = ref<ActivityEntry[]>([]);
  const reminderSchedule = ref<ReminderSchedule | null>(null);

  // Card terminals. Cached for 5 minutes: the drawer refetches on
  // open, and the "Refresh list of terminals" link bypasses the cache with
  // { force: true }. hasTerminals gates the "Credit Card Terminal" method.
  const TERMINALS_CACHE_MS = 5 * 60 * 1000;
  const terminals = ref<Terminal[]>([]);
  const terminalsFetchedAt = ref<number | null>(null);
  // Cache is per-auction: without this key, switching auctions inside the
  // cache window would reuse the previous auction's device list and could
  // send a charge to the wrong terminal.
  const terminalsAuctionUuid = ref<string | null>(null);
  const terminalsState = ref<LoadState>('idle');
  const hasTerminals = computed(() => terminals.value.length > 0);
  // Whether the resolved terminal merchant runs against Neon Pay's sandbox.
  // Sourced from the terminals endpoint; gates the "simulate tap" dev controls
  // so they never render for a live merchant.
  const terminalsSandbox = ref(false);
  let terminalsRequestId = 0;

  // Persist the last-selected terminal per auction so the drawer can restore it
  // on reopen. Keyed by auction UUID (each auction resolves its own merchant),
  // and always re-validated against the live terminal list on restore — a saved
  // id for a since-deactivated device is simply ignored, never charged.
  const TERMINAL_PREF_PREFIX = 'neon.settlement.terminal:';
  function persistSelectedTerminal(uuid: string, terminalId: number): void {
    try {
      localStorage.setItem(`${TERMINAL_PREF_PREFIX}${uuid}`, String(terminalId));
    } catch {
      // localStorage unavailable (private mode / quota) — preference is best-effort.
    }
  }
  function readPersistedTerminal(uuid: string): number | null {
    try {
      const raw = localStorage.getItem(`${TERMINAL_PREF_PREFIX}${uuid}`);
      if (raw === null) return null;
      const id = Number(raw);
      return Number.isInteger(id) ? id : null;
    } catch {
      return null;
    }
  }

  async function fetchTerminals(uuid: string, { force = false }: { force?: boolean } = {}): Promise<void> {
    const isFresh =
      terminalsAuctionUuid.value === uuid &&
      terminalsFetchedAt.value !== null &&
      Date.now() - terminalsFetchedAt.value < TERMINALS_CACHE_MS;
    if (!force && isFresh) return;

    const requestId = ++terminalsRequestId;
    if (terminalsAuctionUuid.value !== uuid) {
      terminals.value = [];
      terminalsFetchedAt.value = null;
      terminalsAuctionUuid.value = uuid;
      terminalsSandbox.value = false;
    }

    terminalsState.value = 'loading';
    try {
      const next = await settlementApi.listTerminals(uuid);
      if (requestId !== terminalsRequestId) return;
      terminals.value = next.terminals;
      terminalsSandbox.value = next.sandbox;
      terminalsFetchedAt.value = Date.now();
      terminalsState.value = 'success';
    } catch {
      if (requestId !== terminalsRequestId) return;
      // Leave the last-known list intact; an empty list simply hides the
      // terminal option rather than surfacing a blocking error.
      terminalsState.value = 'error';
    }
  }
  // Keep the code the surface maps to copy, plus whether the interceptor has
  // already spoken for this failure. A non-ApiError (network drop, thrown
  // TypeError) carries no code, so it leaves the field null and the caller
  // falls back to its own generic copy.
  function recordActionError(e: unknown): void {
    lastActionError.value = e instanceof ApiError
      ? { code: e.code, reported: wasToastReported(e) }
      : null;
  }

  function logActivity(icon: string, text: string): void {
    activity.value = [{ id: newKey(), icon, text, actor: 'You', at: new Date().toISOString() }, ...activity.value];
  }

  // The console's money, in the plan's buckets (Collected / Outstanding /
  // Expected revenue, with the clearing and unconfirmed qualifiers). The
  // server's `eventMoney` is the figure; the roster projection is the same
  // buckets for a payload that predates it, never a second definition.
  const summary = computed<CollectionSummary>(() => {
    const counts = zeroCounts();
    for (const inv of invoices.value) counts[inv.status] += 1;
    return { ...(eventMoney.value ?? consoleMoneyFromRoster(invoices.value)), counts };
  });

  // Work queue = every invoice that still needs a human: a not-yet-collected
  // status AND money left to collect. Membership is the same across modes — the
  // seed for each mode produces the right rows (a lone decline for auto-charge,
  // the full roster for review/collect). The Console reframes how this queue
  // reads per mode.
  const workQueue = computed<PaymentInvoice[]>(() =>
    invoices.value.filter((inv) => needsWork(inv)),
  );

  // Resolved bidders: collected, or closed out via refund / reassignment. They
  // belong in the Settled panel (with their own pill) rather than vanishing.
  const settled = computed<PaymentInvoice[]>(() =>
    invoices.value.filter((inv) => isResolved(inv.status)),
  );

  // The bidders table's rows, at bidder grain. A `computed` and never a `ref`
  // an action writes: every figure on it is derived on read, so one refresh
  // after any admin action repaints the whole table with no reload. A cached
  // copy here is the single way this feature breaks AC-17 from the FE side.
  // /* §9.1 AC-17 — derived on read */
  const bidderRollups = computed<BidderRollup[]>(() =>
    includeUninvoicedOfferees(rollUpByBidder(invoices.value, outstandingOffers.value), bidders.value, outstandingOffers.value),
  );

  let _refreshTimer: ReturnType<typeof setTimeout> | null = null;
  function applyFrame(frame: WsClientFrame): void {
    if (!frame.type?.startsWith('invoice.') && !frame.type?.startsWith('offer.')) return;
    if (_refreshTimer !== null) clearTimeout(_refreshTimer);
    _refreshTimer = setTimeout(() => {
      _refreshTimer = null;
      void refresh();
    }, 300);
  }

  // The framing mode comes from the auction's own config: no-card auctions
  // collect manually; card-required auctions either auto-charge at close or
  // offer self-checkout (review). Falls back to the current mode when the
  // event hasn't loaded. The dev switcher overrides this for design review.
  function deriveModeFromConfig(): SettlementMode {
    const ev = useCurrentAuction().event;
    if (!ev) return mode.value;
    if (ev.paymentMethodRequirement === 'allow_pay_later') return 'collect';
    if (ev.autoCheckoutMode === 'auto_charge') return 'auto_charge';
    return 'review_charge';
  }

  function auditEntryToActivity(entry: AuditLogEntry): ActivityEntry {
    const actorLabel = entry.actorType === 'system' ? 'System'
      : entry.actorType === 'admin' ? 'Admin'
      : entry.actorType === 'staff' ? 'Staff'
      : 'Bidder';

    const atLabel = new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
      Math.round((new Date(entry.at).getTime() - Date.now()) / 60000),
      'minute',
    );

    // Every action `writeAuditLog` can emit into an event-scoped feed has an
    // entry here. An action missing from these maps falls through to the raw
    // `snake_case` string below, which is not something to show an admin — so
    // adding an audit action in the backend means adding it here too.
    //
    // Deliberately absent: `feature_flag.*`, `donor_*` and `event_deleted` are
    // written with no org or no auctionEventId, so the org+event-scoped audit
    // query can never return them to this feed.
    const iconMap: Record<string, string> = {
      event_closed: 'pi pi-flag-fill',
      invoice_generated: 'pi pi-file',
      checkout_emails_sent: 'pi pi-send',
      invoice_charged: 'pi pi-check-circle',
      invoice_charge_processing: 'pi pi-spin pi-spinner',
      invoice_charge_failed: 'pi pi-exclamation-triangle',
      invoice_offline_payment: 'pi pi-wallet',
      invoice_refunded: 'pi pi-undo',
      invoice_lines_declined: 'pi pi-times',
      receipts_send_batch: 'pi pi-envelope',
      invoice_receipt_sent: 'pi pi-envelope',
      checkout_email_resent: 'pi pi-send',
      next_offer_triggered: 'pi pi-arrows-h',
      next_offer_no_candidate: 'pi pi-info-circle',
      offer_declined: 'pi pi-times-circle',
      offer_accepted: 'pi pi-check',
      offer_cancelled: 'pi pi-ban',

      // Reassignment and sale cancellation.
      item_reassigned: 'pi pi-arrows-h',
      reassign_offer_sent: 'pi pi-send',
      item_sale_canceled: 'pi pi-ban',
        item_sold_to_bidder: 'pi pi-check-circle',
        item_offered_to_bidder: 'pi pi-send',
        admin_bid_placed: 'pi pi-shield',

      // Invoice and money.
      invoice_fee_coverage_set: 'pi pi-percentage',
      invoice_fee_coverage_recomputed: 'pi pi-percentage',
      invoice_donation_set: 'pi pi-heart',
      invoice_charge_canceled: 'pi pi-ban',
      invoice_charge_indeterminate: 'pi pi-question-circle',
      invoice_charge_resolved_succeeded: 'pi pi-check-circle',
      invoice_charge_resolved_failed: 'pi pi-exclamation-triangle',
      invoice_paid_webhook: 'pi pi-check-circle',
      invoice_failed_webhook: 'pi pi-exclamation-triangle',
      invoice_processing_webhook: 'pi pi-spin pi-spinner',
      invoice_refund_confirmed_webhook: 'pi pi-undo',

      // Auction lifecycle and roster.
      event_created: 'pi pi-plus',
      event_updated: 'pi pi-pencil',
      event_published: 'pi pi-globe',
      event_unpublished: 'pi pi-eye-slash',
      event_opened: 'pi pi-play',
      event_paused: 'pi pi-pause',
      event_resumed: 'pi pi-play',
      event_extended: 'pi pi-clock',
      registration_opened: 'pi pi-user-plus',
      registration_closed: 'pi pi-user-minus',
      reminder_dispatch: 'pi pi-bell',
      reminders_sent_activation: 'pi pi-bell',
      reminders_sent_payment_followup: 'pi pi-bell',
      bidder_registered_walkup: 'pi pi-user-plus',
      bid_voided: 'pi pi-times-circle',
      broadcast_email_sent: 'pi pi-envelope',

      // Catalog.
      item_created: 'pi pi-plus',
      item_duplicated: 'pi pi-copy',
      item_updated: 'pi pi-pencil',
      item_published: 'pi pi-globe',
      item_made_live: 'pi pi-play',
      item_unpublished: 'pi pi-eye-slash',
      item_withdrawn: 'pi pi-ban',
      item_restored: 'pi pi-undo',
      item_closed: 'pi pi-flag',
      item_reopened: 'pi pi-replay',
      item_deleted: 'pi pi-trash',
      catalog_reordered: 'pi pi-sort-alt',
      category_created: 'pi pi-tag',
      category_updated: 'pi pi-tag',
      category_deleted: 'pi pi-tag',

      // Bundles.
      bundle_created: 'pi pi-objects-column',
      bundle_updated: 'pi pi-objects-column',
      bundle_published: 'pi pi-globe',
      bundle_made_live: 'pi pi-play',
      bundle_unpublished: 'pi pi-eye-slash',
      bundle_withdrawn: 'pi pi-ban',
      bundle_restored: 'pi pi-undo',
      bundle_reopened: 'pi pi-replay',
      bundle_deleted: 'pi pi-trash',
      bundle_member_added: 'pi pi-plus',
      bundle_member_removed: 'pi pi-minus',
      bundle_member_withdrawn: 'pi pi-ban',
      bundle_member_restored: 'pi pi-replay',

      // Ops.
      crm_push_retry: 'pi pi-sync',
      crm_reconcile_all: 'pi pi-sync',
      crm_reconcile_batch: 'pi pi-sync',
      crm_reconcile_bidder: 'pi pi-sync',
      notification_template_updated: 'pi pi-file-edit',
      notification_template_reset: 'pi pi-replay',
    };

    const textMap: Record<string, string> = {
      event_closed: 'Auction closed',
      invoice_generated: 'Invoices generated for winners',
      checkout_emails_sent: 'Checkout links emailed to winners',
      invoice_charged: 'Payment collected',
      invoice_charge_processing: 'Charge processing',
      invoice_charge_failed: 'Charge failed',
      invoice_offline_payment: 'Offline payment recorded',
      invoice_refunded: 'Refund issued',
      invoice_lines_declined: 'Items declined',
      receipts_send_batch: 'Receipts emailed',
      invoice_receipt_sent: 'Receipt emailed',
      checkout_email_resent: 'Checkout link resent',
      next_offer_triggered: 'Item offered to next bidder',
      next_offer_no_candidate: 'No candidate for next offer',
      offer_declined: 'Offer declined by bidder',
      offer_accepted: 'Offer accepted by bidder',
      offer_cancelled: 'Offer cancelled',

      // Reassignment and sale cancellation.
      item_reassigned: 'Item reassigned',
      reassign_offer_sent: 'Reassign offer sent',
      item_sale_canceled: 'Item withdrawn',
        item_sold_to_bidder: 'Item sold to bidder',
        item_offered_to_bidder: 'Item offered to bidder',
        admin_bid_placed: 'Bid placed for bidder',

      // Invoice and money.
      invoice_fee_coverage_set: 'Invoice fee coverage set',
      invoice_fee_coverage_recomputed: 'Fee coverage recomputed',
      invoice_donation_set: 'Add-on donation set',
      invoice_charge_canceled: 'Charge canceled',
      invoice_charge_indeterminate: 'Charge outcome unknown',
      invoice_charge_resolved_succeeded: 'Stuck charge resolved as paid',
      invoice_charge_resolved_failed: 'Stuck charge resolved as failed',
      invoice_paid_webhook: 'Payment confirmed',
      invoice_failed_webhook: 'Payment failed',
      invoice_processing_webhook: 'Payment processing',
      invoice_refund_confirmed_webhook: 'Refund confirmed',

      // Auction lifecycle and roster.
      event_created: 'Auction created',
      event_updated: 'Auction updated',
      event_published: 'Auction published',
      event_unpublished: 'Auction unpublished',
      event_opened: 'Auction opened',
      event_paused: 'Auction paused',
      event_resumed: 'Auction resumed',
      event_extended: 'Auction extended',
      registration_opened: 'Registration opened',
      registration_closed: 'Registration closed',
      reminder_dispatch: 'Reminders dispatched',
      reminders_sent_activation: 'Activation reminders sent',
      reminders_sent_payment_followup: 'Payment reminders sent',
      bidder_registered_walkup: 'Walk-up bidder registered',
      bid_voided: 'Bid voided',
      broadcast_email_sent: 'Email broadcast sent',

      // Catalog.
      item_created: 'Item created',
      item_duplicated: 'Item duplicated',
      item_updated: 'Item updated',
      item_published: 'Item published',
      item_made_live: 'Item made live',
      item_unpublished: 'Item unpublished',
      item_withdrawn: 'Item withdrawn',
      item_restored: 'Item restored',
      item_closed: 'Item closed',
      item_reopened: 'Item reopened',
      item_deleted: 'Item deleted',
      catalog_reordered: 'Catalog reordered',
      category_created: 'Category created',
      category_updated: 'Category updated',
      category_deleted: 'Category deleted',

      // Bundles.
      bundle_created: 'Bundle created',
      bundle_updated: 'Bundle updated',
      bundle_published: 'Bundle published',
      bundle_made_live: 'Bundle made live',
      bundle_unpublished: 'Bundle unpublished',
      bundle_withdrawn: 'Bundle withdrawn',
      bundle_restored: 'Bundle restored',
      bundle_reopened: 'Bundle reopened',
      bundle_deleted: 'Bundle deleted',
      bundle_member_added: 'Item added to bundle',
      bundle_member_removed: 'Item removed from bundle',
      bundle_member_withdrawn: 'Bundle item withdrawn',
      bundle_member_restored: 'Bundle item restored',

      // Ops.
      crm_push_retry: 'CRM push retried',
      crm_reconcile_all: 'CRM reconciliation run',
      crm_reconcile_batch: 'CRM batch reconciled',
      crm_reconcile_bidder: 'CRM bidder reconciled',
      notification_template_updated: 'Notification template updated',
      notification_template_reset: 'Notification template reset',
    };

    // Reassignment writes the same action names for items and bundles and says
    // which in `entityType`, so the few whose label names the lot read it here.
    const bundleTextMap: Record<string, string> = {
      item_reassigned: 'Bundle reassigned',
      item_sale_canceled: 'Bundle withdrawn',
        item_sold_to_bidder: 'Bundle sold to bidder',
        item_offered_to_bidder: 'Bundle offered to bidder',
    };
    const text =
      (entry.entityType === 'bundle' ? bundleTextMap[entry.action] : undefined) ??
      textMap[entry.action] ??
      entry.action;

    return {
      id: String(entry.id),
      icon: iconMap[entry.action] ?? 'pi pi-info-circle',
      text,
      actor: actorLabel,
      at: atLabel,
    };
  }

  // Gate the audit-log fetch on the activity-feed flag. When the flag is OFF the
  // backend returns 403 ACTIVITY_FEED_DISABLED, and the shared httpClient
  // interceptor fires a generic "Permission denied" toast before a local catch
  // can swallow it — so skip the network call entirely (mirrors the ConsolePage
  // flags.activityFeed gate). Exposed separately so the ConsolePage can refetch
  // when the asynchronously-hydrated flag flips ON after the initial load (a
  // supervisor toggle broadcast, or late shell hydration) — otherwise the
  // Activity section would appear but stay empty. The try/catch is a secondary
  // safety net for other failures when the flag is ON.
  async function loadActivity(uuid: string): Promise<void> {
    if (!useAdminSession().activityFeed) {
      activity.value = [];
      return;
    }
    try {
      const logItems = await settlementApi.fetchAuditLog(uuid);
      activity.value = logItems.map(auditEntryToActivity);
    } catch {
      activity.value = [];
    }
  }

  // ── Reassignment ────────────────────────────────────────────────────
  // Every read below is derived from the settlement wire, which carries each
  // line's photo, item status, bid count, full bid stack, pending offer and
  // new owner. Nothing here caches item state of its own: an item's fate is
  // decided by the backend transaction and re-read by refresh(), so the
  // surfaces cannot drift from what actually committed.
  //
  // The two writes are single transactional POSTs:
  //   POST /admin/invoices/:invoiceUuid/reassign  (offer | now)
  //   POST /admin/auctions/:uuid/{items/:itemId|bundles/:bundleId}/unsold  (cancel sale)
  // Both REQUIRE an Idempotency-Key covering the whole action including the
  // notify choice — see reassignItem's `idempotencyKey` argument.

  // Indexed once per roster rather than scanned per lookup: the console's
  // items table and the drawers call these once per row on every render, and a
  // linear scan there is quadratic in the auction's line count.
  const linesById = computed(() => {
    const out = new Map<string, { invoice: PaymentInvoice; line: PaymentLineItem }>();
    for (const invoice of invoices.value) {
      for (const line of invoice.lineItems) out.set(line.lineId, { invoice, line });
    }
    return out;
  });

  // A lot can appear on more than one line once it has been reassigned (the
  // old owner's declined line and the new owner's line both charge for it), so
  // prefer a line that actually carries the stack rather than the first hit.
  // Keyed by lot key, not the bare id: an item and a bundle can share a number.
  const linesByLot = computed(() => {
    const out = new Map<LotKey, PaymentLineItem>();
    for (const invoice of invoices.value) {
      for (const line of invoice.lineItems) {
        const lot = lotRefOfLine(line);
        if (lot === null) continue;
        const key = lotKeyOf(lot);
        const prev = out.get(key);
        if (!prev || (!prev.bidStack?.length && line.bidStack?.length)) {
          out.set(key, line);
        }
      }
    }
    return out;
  });

  // One entry per auction item, carrying the line that currently holds it —
  // what every item-centric surface wants, as opposed to the per-line roster.
  //
  // The survivor is chosen on the structural fact "is this line still live",
  // NOT on `reassignedTo`. `reassignedTo` is a derived read of the item's
  // current high bidder, so it is null on a superseded line whose item moved on
  // again, and non-null on lines with no successor row here at all (voiding a
  // bid can hand the high bid to somebody who has no invoice line). What a
  // reassignment actually writes is `declined` — the same flag the server
  // refuses on with "line item has already been released".
  const itemLines = computed<SettlementItemLine[]>(() => {
    // Insertion-ordered: an item keeps the position it held before it was
    // reassigned instead of jumping to wherever the new winner's invoice sits.
    // `releasedSiblings` counts, not collects: the tally is the same whatever
    // order the roster arrives in, and it is all "was this item reassigned?"
    // needs. Invoices arrive in invoice-id order, which is creation order and
    // not release order, so anything order-sensitive here would be wrong for
    // any chain whose newest holder was invoiced first.
    const byItem = new Map<
      string,
      Omit<SettlementItemLine, 'wasReassigned'> & { releasedSiblings: number }
    >();
    for (const invoice of invoices.value) {
      for (const line of invoice.lineItems) {
        if (line.kind !== 'auction_item') continue;
        // A line that names no lot can never be collapsed with anything: it
        // keys off its own line id.
        const lot = lotRefOfLine(line);
        const key = lot === null ? `line:${line.lineId}` : lotKeyOf(lot);
        const prev = byItem.get(key);
        if (!prev) {
          byItem.set(key, { invoice, line, releasedSiblings: 0 });
          continue;
        }
        //   1. a live line on a non-void invoice beats released history — the new winner's
        //      line, and A -> B -> C still leaves exactly one of them;
        //   2. among declined-only lines (reassigned, then the sale canceled),
        //      the one the item was NOT released away from — no `reassignedTo`
        //      — is the terminal holder;
        //   3. otherwise keep the first seen, so the order is stable.
        const live = !line.declined && invoice.status !== InvoicePaymentStatusSchema.enum.void;
        const previousLive = !prev.line.declined && prev.invoice.status !== InvoicePaymentStatusSchema.enum.void;
        const wins = (live && !previousLive) ||
          (live === previousLive && line.declined === prev.line.declined &&
            prev.line.reassignedTo !== null && line.reassignedTo === null);
        if (wins) {
          byItem.set(key, {
            invoice,
            line,
            releasedSiblings: prev.releasedSiblings + (prev.line.declined ? 1 : 0),
          });
        } else if (line.declined) {
          prev.releasedSiblings += 1;
        }
      }
    }
    // "Reassigned" is a statement about a LIVE successor. A declined survivor
    // with declined siblings is an item whose sale was later canceled, and
    // "Reassigned" would be the wrong word for it.
    return [...byItem.values()].map(({ releasedSiblings, ...row }) => ({
      ...row,
      wasReassigned: !row.line.declined && releasedSiblings > 0,
    }));
  });

  function findLine(lineId: string): { invoice: PaymentInvoice; line: PaymentLineItem } | null {
    return linesById.value.get(lineId) ?? null;
  }

  function findLineForLot(lot: LotRef): PaymentLineItem | null {
    return linesByLot.value.get(lotKeyOf(lot)) ?? null;
  }

    function lotDetailFor(lot: LotRef): LotDetail | null {
        const key = lotKeyOf(lot);
        const row = itemLines.value.find(({ line }) => {
            const ref = lotRefOfLine(line);
            return ref !== null && lotKeyOf(ref) === key;
        });
        if (row && !row.line.declined && row.invoice.status !== InvoicePaymentStatusSchema.enum.void) {
            return lineLotDetail(lot, row);
        }
        const companions: CompanionLot[] = [...strandedLots.value, ...uninvoicedLots.value, ...unbidLots.value];
        const companion = companions.find(candidate => {
            const ref = lotRefOfLine(candidate);
            return ref !== null && lotKeyOf(ref) === key;
        });
        const offer = outstandingOffers.value.find(candidate => {
            const ref = lotRefOfLine(candidate);
            return ref !== null && lotKeyOf(ref) === key;
        }) ?? null;
        if (companion) return companionLotDetail(lot, companion, offer);
        return row ? lineLotDetail(lot, row) : null;
    }

  function photoFor(lot: LotRef | null): PhotoVariant | null {
    if (lot === null) return null;
    return lotDetailFor(lot)?.photo ?? null;
  }

  // Everyone who bid on this item: one row per bidder at their highest
  // non-voided bid (a voided bid is not a bid any more), highest first.
  // This is what the item drawer lists, as context for choosing a target.
  function bidderStackFor(lot: LotRef): ReassignCandidate[] {
    return lotDetailFor(lot)?.bidStack ?? findLineForLot(lot)?.bidStack ?? [];
  }

  // The item's own bidders offered as targets: the same stack minus the
  // current winner, each at their own bid. An EMPTY result does not mean the
  // item cannot be reassigned — the dialog's pool is the whole event roster,
  // and this list only decides who is priced at their own bid.
  function eligibleStackFor(lot: LotRef, excludeBidderUuid: string | null): ReassignCandidate[] {
    return bidderStackFor(lot).filter(c => c.bidderUuid !== excludeBidderUuid);
  }

  // The winner-slot view model for a line. Live state only: a pending offer, or
  // a completed transfer.
  //
  // There used to be a third branch — a session-local "Offer canceled" marker
  // keyed by lineId, because the settlement payload could not tell an offer
  // canceled a moment ago from a lot that was never offered. The server names
  // that state now (`line.disposition` is `offer_cancelled`, ADR-015 §5), so
  // the marker is gone: keeping both would make a cancelled offer read one way
  // before a reload and another after. Do not reintroduce it — a disposition
  // question is answered by the disposition. /* §3.4 */
  function reassignmentFor(lineId: string): ItemReassignment | null {
    const found = findLine(lineId);
    if (!found) return null;
    const { line } = found;

    if (line.pendingOffer) {
      return {
        mode: 'offer',
        targetBidderUuid: line.pendingOffer.bidderUuid,
        targetName: line.pendingOffer.bidderName,
        amountCents: line.pendingOffer.amountCents,
        expiresAt: line.pendingOffer.expiresAt,
      };
    }
    if (line.reassignedTo) {
      return {
        mode: 'now',
        targetBidderUuid: line.reassignedTo.bidderUuid,
        targetName: line.reassignedTo.bidderName,
        amountCents: line.reassignedTo.amountCents,
        expiresAt: null,
      };
    }
    return null;
  }

  // ── Custody trails (ADR-015 §5) ────────────────────────────────────────
  //
  // One lot's ordered history, keyed by lot key and fetched per lot
  // because only the item drawer reads it — loading every lot's trail with the
  // roster would pull the whole event's history to render one drawer.
  //
  // The events are cached exactly as the server ordered them. Nothing here
  // sorts, filters or appends: an entry this client invented would be the one
  // line of the trail no record backs.
  const custodyByLot = ref<Map<LotKey, ItemCustodyHistory>>(new Map());
  const custodyState = ref<LoadState>('idle');
  // Guards the same overlap the roster's generation counter does: opening the
  // drawer on one lot and then another leaves two reads in flight, and without
  // this the slower one paints the wrong lot's trail.
  let custodyRequestId = 0;

  function custodyFor(lot: LotRef | null): ItemCustodyHistory | null {
    if (lot === null) return null;
    return custodyByLot.value.get(lotKeyOf(lot)) ?? null;
  }

  // `force` is for after an admin action: the trail just grew an entry, and the
  // cached copy is the state before it.
  async function loadItemCustody(
    lot: LotRef,
    { force = false }: { force?: boolean } = {},
  ): Promise<void> {
    const uuid = auctionUuid.value;
    if (!uuid) return;
    const key = lotKeyOf(lot);
    if (!force && custodyByLot.value.has(key)) {
      custodyState.value = 'success';
      return;
    }
    const requestId = ++custodyRequestId;
    custodyState.value = 'loading';
    try {
      const history = await settlementApi.fetchItemCustody(uuid, lot);
      if (requestId !== custodyRequestId) return;
      const next = new Map(custodyByLot.value);
      next.set(key, history);
      custodyByLot.value = next;
      custodyState.value = 'success';
    } catch (e) {
      if (requestId !== custodyRequestId) return;
      recordActionError(e);
      custodyState.value = 'error';
    }
  }

  // ── Reassign targets (ADR-012 §7) ──────────────────────────────────────
  //
  // Who a lot can go to, and where it would land for each of them. Cached and
  // guarded exactly like the custody trail above: per lot, because only the
  // open dialog reads it, and generation-counted because opening the dialog on
  // one lot and then another leaves two reads in flight.
  //
  // Nothing here filters or re-prices the rows. The server decides both the
  // pool and each landing from the same open-set predicate the reassign
  // transaction runs, so a client that narrowed the list would be re-imposing
  // the blocks AC-1 removed.
  const targetsByLot = ref<Map<LotKey, ReassignTarget[]>>(new Map());
  const targetsState = ref<LoadState>('idle');
  let targetsRequestId = 0;
    const placementPreviewLots = new Set<LotKey>();

  function reassignTargetsFor(lot: LotRef | null): ReassignTarget[] | null {
    if (lot === null) return null;
    return targetsByLot.value.get(lotKeyOf(lot)) ?? null;
  }

    async function readTargets(lot: LotRef, placement: boolean, force: boolean): Promise<void> {
        const uuid = auctionUuid.value;
        if (!uuid) return;
        const key = lotKeyOf(lot);
        if (!force && targetsByLot.value.has(key) && (!placement || placementPreviewLots.has(key))) {
            targetsState.value = 'success';
            return;
        }
        const requestId = ++targetsRequestId;
        targetsState.value = 'loading';
        try {
            const fetch = placement ? settlementApi.fetchPlacementTargets : settlementApi.fetchReassignTargets;
            const response = await fetch(uuid, lot);
            if (requestId !== targetsRequestId) return;
            targetsByLot.value = new Map(targetsByLot.value).set(key, response.targets);
            if (placement) placementPreviewLots.add(key);
            targetsState.value = 'success';
        } catch (error) {
            if (requestId !== targetsRequestId) return;
            recordActionError(error);
            targetsState.value = 'error';
        }
    }

    async function loadReassignTargets(lot: LotRef, { force = false }: { force?: boolean } = {}): Promise<void> {
        await readTargets(lot, false, force);
    }

    async function loadPlacementTargets(lot: LotRef, { force = false }: { force?: boolean } = {}): Promise<void> {
        await readTargets(lot, true, force);
    }

    function placementTargetsFor(lot: LotRef | null, inheritedPriceCents: number): TargetRow[] | null {
        return reassignTargetsFor(lot)?.map(target => toTargetRow(target, inheritedPriceCents)) ?? null;
    }

  // Both per-lot caches answer questions about where a lot sits, so an action
  // that moves it invalidates both together — a landing preview computed
  // before the move is as stale as a trail missing its newest entry.
  function forgetLotCaches(lot: LotRef | null): void {
    if (lot === null) return;
    const key = lotKeyOf(lot);
    const trails = new Map(custodyByLot.value);
    trails.delete(key);
    custodyByLot.value = trails;
    const targets = new Map(targetsByLot.value);
    targets.delete(key);
    targetsByLot.value = targets;
        placementPreviewLots.delete(key);
  }

  // Cancel sale — the terminal resolution for an item nobody will pay for.
  // Declines the winner's line so nothing is owed and sets the item to
  // `unsold`.
  function isUnsold(lineId: string): boolean {
    return findLine(lineId)?.line.itemStatus === 'unsold';
  }

  // `unsold` is the status of BOTH a canceled sale and a lot that closed with no
  // qualifying bid, so it cannot distinguish them and deliberately does not try
  // (isUnsold above drives the Winner column and the kebab gating, which want
  // both cases). The timestamp is the only discriminator, so any surface that
  // needs to say "this sale was canceled" has to read it.
  function saleCanceledAtFor(lineId: string): string | null {
    return findLine(lineId)?.line.saleCanceledAt ?? null;
  }

  function isSaleCanceled(lineId: string): boolean {
    return saleCanceledAtFor(lineId) !== null;
  }

  // `idempotencyKey` follows the same lifetime rule as reassignItem's: a caller
  // retrying one submission passes the original key; omitting it is for
  // one-shot confirms.
  async function markUnsold(lineId: string, notify = true, idempotencyKey?: string): Promise<boolean> {
    const found = findLine(lineId);
    const lot = found ? lotRefOfLine(found.line) : null;
    return lot === null ? false : withdrawLot(lot, notify, idempotencyKey);
  }

  // Withdraw a closed lot for good: releases any unpaid holder, ends a
  // standing offer, and takes it out of every sale. Works on a lot with no
  // invoice line too, which is how an Unsold lot is withdrawn.
  async function withdrawLot(lot: LotRef, notify = true, idempotencyKey?: string): Promise<boolean> {
    if (!auctionUuid.value) return false;
    actionState.value = 'loading';
    lastActionError.value = null;
    try {
      await settlementApi.cancelSale(auctionUuid.value, lot, idempotencyKey ?? newKey(), notify);
      await refresh();
      actionState.value = 'success';
      return true;
    } catch (e) {
      actionState.value = 'error';
      recordActionError(e);
      // Same rule as reassignItem: a 409 means the item resolved underneath the
      // caller, so the state on screen is already wrong. Re-read it, or the
      // admin's next action is aimed at data the server has moved on from.
      if (e instanceof ApiError && e.status === 409) await refresh();
      return false;
    }
  }

  // Withdraw a pending offer: the item returns to needing attention with the
  // "Offer canceled" context line.
  //
  // Addressed by the offer's own public id, which is the grain the route works
  // at. Callers hold that id already — either off the payload's
  // `outstandingOffers` array or off the `pendingOffer` on the line they are
  // rendering — so resolving a line back to its offer here would only be a
  // detour through data an offer does not depend on.
  async function cancelOffer(offerPublicId: string): Promise<boolean> {
    actionState.value = 'loading';
    lastActionError.value = null;
    try {
      await settlementApi.cancelOffer(offerPublicId);
      await refresh();
      actionState.value = 'success';
      return true;
    } catch (e) {
      actionState.value = 'error';
      recordActionError(e);
      // A 409 here means the offer already resolved (accepted, declined or
      // expired). Nothing was canceled, but the stale pendingOffer must go, and
      // the refresh brings back the disposition that says which of the three
      // actually happened.
      if (e instanceof ApiError && e.status === 409) await refresh();
      return false;
    }
  }

  // Reassign the item on `lineId` to another bidder, then re-read true state.
  //
  // `idempotencyKey` must identify the SUBMIT, not the attempt: the key covers
  // the whole action including the notify choice, so retrying a failed submit
  // has to send the same key (a fresh one would commit a second, genuinely
  // different reassignment) while a changed target/mode/notify must not.
  // Callers own that lifetime; the default is for one-shot callers.
  //
  // A 409 means the item moved underneath the caller (design §edge 5): the
  // refresh still runs, so whatever the caller renders next is true, and
  // `conflict` lets it say so rather than reporting a generic failure.
    async function performPlacement(lot: LotRef | null, send: () => Promise<ReassignItemResult>): Promise<PlacementOutcome> {
        actionState.value = 'loading';
        lastActionError.value = null;
        try {
            const result = await send();
            forgetLotCaches(lot);
            await refresh();
            actionState.value = 'success';
            return { ok: true, conflict: false, result, collection: result.collection ?? null };
        } catch (error) {
            actionState.value = 'error';
            recordActionError(error);
            const conflict = error instanceof ApiError && error.status === 409;
            if (conflict) {
                forgetLotCaches(lot);
                await refresh();
            }
            return { ok: false, conflict, result: null, collection: null };
        }
    }

    async function submitPlacement(args: SubmitPlacementArgs): Promise<PlacementOutcome> {
        const failure: PlacementOutcome = { ok: false, conflict: false, result: null, collection: null };
        const key = args.idempotencyKey ?? newKey();
        const fields = placementFields(args);
        if (args.lot) {
            const detail = lotDetailFor(args.lot);
            if (!auctionUuid.value || !detail || !UNSOLD_STATES.includes(detail.saleState)) return failure;
            const body = { ...fields, lot: args.lot, priceBasis: args.priceBasis, priceCents: args.priceCents };
            const uuid = auctionUuid.value;
            return performPlacement(args.lot, () => settlementApi.placeLot(uuid, key, body));
        }
        const found = findLine(args.lineId);
        if (!found) return failure;
        const body = { ...fields, lineItemPublicId: args.lineId };
        return performPlacement(lotRefOfLine(found.line), () => settlementApi.reassign(found.invoice.invoiceUuid, key, body));
    }

    async function reassignItem(args: {
        lineId: string;
        targetBidderUuid: string;
        mode: 'offer' | 'now';
        notify: boolean;
        idempotencyKey?: string;
    }): Promise<ReassignOutcome> {
        const { ok, conflict, result } = await submitPlacement(args);
        return { ok, conflict, result };
    }

  // Backend-driven: pull the enriched settlement roster from
  // GET /admin/auctions/:uuid/settlement (status derived server-side into
  // our PaymentStatus vocab). An empty array is a real state (e.g. a
  // just-closed auction whose invoices are still generating) — keep it
  // empty so the Console can retry rather than show another auction's
  // seed data.
  //
  // Three invariants, all load-bearing:
  //
  // 1. Every write is gated on still being the newest request. Two roster reads
  //    overlap routinely — an action awaits refresh() while the WebSocket frame
  //    for the same action debounces into a second one — and without the gate
  //    the SLOWER response wins and the console shows pre-action state.
  //    Re-check after EVERY await, not just the first: a stale continuation can
  //    resume between any two of them.
  //
  // 2. A failed refresh must not destroy `invoices` or touch `loadState` — as
  //    long as a first load has already resolved that gate. The console's items
  //    table and its row controls live inside a `ready`-gated subtree, so
  //    blanking either on a background failure leaves an admin with zero rows
  //    and no controls while the action that triggered the refresh still
  //    reports success — recoverable only by reloading the page. A failed FIRST
  //    load is different: nothing is on screen to protect, and an error state
  //    with a "Try again" is the correct thing to show.
  //
  // 3. Whichever read ends up newest resolves the first load's `loadState`, even
  //    when that read is a refresh (`firstLoadPending`). Both invariants above
  //    pull the other way otherwise: the gate the superseded load owned is never
  //    written, and the console sits on "Loading settlement…" — which offers no
  //    Try again — until the page is reloaded.
  // One read writes the roster AND its companions together, so the feed
  // can never render offers or stranded lots from a different read than the
  // invoices beside them. A payload missing a companion array reads as "none"
  // rather than as a failure: an older backend, or a bare-array response, still
  // renders a correct console — with no stranded rows rather than invented ones.
  function applySettlementPayload(payload: unknown): void {
    const source = (Array.isArray(payload) ? { items: payload } : payload ?? {}) as
      Partial<SettlementPayload>;
    invoices.value = (source.items ?? []) as PaymentInvoice[];
    eventMoney.value = source.eventMoney ?? null;
    bidders.value = source.bidders ?? [];
    uninvoicedLots.value = source.uninvoicedLots ?? [];
    unbidLots.value = source.unbidLots ?? [];
    outstandingOffers.value = source.outstandingOffers ?? [];
    strandedLots.value = source.strandedLots ?? [];
    refundFailures.value = source.refundFailures ?? [];
  }

  async function fetchRoster(uuid: string, { isRefresh }: { isRefresh: boolean }): Promise<void> {
    const requestId = ++rosterRequestId;
    if (!isRefresh) firstLoadPending = true;
    const state = isRefresh ? refreshState : loadState;
    state.value = 'loading';
    // Terminal write for this read, adopting an unresolved first load along with
    // it. Nothing else may assign the two states: a bare `state.value = ...`
    // leaves the superseded load's gate hanging forever.
    const settle = (result: LoadState): void => {
      state.value = result;
      if (firstLoadPending) {
        loadState.value = result;
        firstLoadPending = false;
      }
    };
    try {
      const payload = await settlementApi.getSettlement(uuid);
      if (requestId !== rosterRequestId) return;
      applySettlementPayload(payload);
      mode.value = deriveModeFromConfig();
      await loadActivity(uuid);
      if (requestId !== rosterRequestId) return;
      try {
        const schedule = await settlementApi.getReminderSchedule(uuid);
        if (requestId !== rosterRequestId) return;
        reminderSchedule.value = schedule;
      } catch {
        // Non-critical — leave the last-known schedule alone if the endpoint fails.
      }
      settle('success');
    } catch {
      if (requestId !== rosterRequestId) return;
      if (!isRefresh) applySettlementPayload(null);
      settle('error');
    }
  }

  async function load(uuid: string): Promise<void> {
    auctionUuid.value = uuid;
    // A new auction is a different set of lots, so nothing cached per lot
    // survives the switch — a trail keyed by item id would otherwise be read
    // for an item id that now means something else entirely.
    custodyByLot.value = new Map();
    custodyState.value = 'idle';
    targetsByLot.value = new Map();
    targetsState.value = 'idle';
        placementPreviewLots.clear();
        targetsRequestId += 1;
        custodyRequestId += 1;
    await fetchRoster(uuid, { isRefresh: false });
  }

  // Actions hit the real admin endpoints, then re-pull true state so the
  // computeds (summary / workQueue / settled) reflect the server. On a thrown
  // error the action returns false and the drawer toasts the failure.
  async function refresh(): Promise<void> {
    if (auctionUuid.value) await fetchRoster(auctionUuid.value, { isRefresh: true });
  }

  async function charge(invoiceUuid: string, token?: string): Promise<boolean> {
    actionState.value = 'loading';
    try {
      // May 422 for card-on-file without a token (backend ChargeInputSchema
      // requires paymentMethodToken) — that surfaces as a thrown error here.
      await settlementApi.charge(invoiceUuid, newKey(), token);
      await refresh();
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  // Start a terminal charge: fresh idempotency key per attempt so a "Try again"
  // never collides with the prior device session. The invoice lands in
  // 'processing'; the drawer polls getInvoice for the paid/failed resolution,
  // so we deliberately do NOT refresh here (that would flip the drawer out of
  // its waiting state before the tap happens).
  async function chargeTerminal(invoiceUuid: string, terminalId: number): Promise<boolean> {
    actionState.value = 'loading';
    try {
      await settlementApi.terminalCharge(invoiceUuid, { terminalId, idempotencyKey: newKey() });
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  // Cancel a pending terminal charge (processing -> pending). A 409 means the
  // tap already completed: the caller keeps waiting / refreshes rather than
  // treating it as a plain failure, so we surface that distinctly.
  async function cancelTerminal(invoiceUuid: string): Promise<{ ok: boolean; conflict: boolean }> {
    actionState.value = 'loading';
    try {
      await settlementApi.terminalCancel(invoiceUuid);
    } catch (e) {
      actionState.value = 'error';
      return { ok: false, conflict: e instanceof ApiError && e.status === 409 };
    }
    // The cancel walks the invoice back from `processing` to `pending` and
    // fails the charge row, and nothing else tells this console about it — the
    // cancel endpoint publishes no realtime frame, so without pulling the
    // roster the drawer goes on rendering the `processing` the charge left
    // behind and the operator has to hard-refresh to see their own action land
    // (GT1-552). Deliberately AFTER the call rather than in place of the
    // caller's own poll: `chargeTerminal` still must not refresh, or the
    // roster would move under a device that is only just being asked for a card.
    //
    // Outside the try, and its own failure is not the cancel's: the session IS
    // cancelled either way, and reporting otherwise would have the caller keep
    // waiting on a device that has already returned to Ready.
    await refresh();
    actionState.value = 'success';
    return { ok: true, conflict: false };
  }

  async function recordOffline(
    invoiceUuid: string,
    amountCents: number,
    method: OfflineMethod,
    reference?: string,
  ): Promise<boolean> {
    actionState.value = 'loading';
    try {
      await settlementApi.recordOffline(invoiceUuid, newKey(), { amountCents, method, reference });
      await refresh();
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  // ADR-014 / AC-4 — the ONE way a lot leaves an invoice: refund and release
  // commit together, server-side, behind a durable obligation. The only other
  // refund is `supportRefund` below, which is supervisor-only and never moves a
  // lot: a money-only refund from a surface that also moves the lot is the
  // strand-money-halfway path AC-4 closes.
  //
  // `refundedCents` is read from the response rather than from what was asked
  // for: the server refunds each line against its own remaining balance, so a
  // selection of already-refunded lines legitimately releases while moving no
  // money at all. /* §3.1 refund-and-release */
  async function refundAndRelease(
    invoiceUuid: string,
    lineItemUuids: string[],
    reason: string,
  ): Promise<{
    ok: boolean;
    refundedCents: number;
    pending: boolean;
    /**
     * The lines that actually left the invoice. Each line is an independent
     * operation with its own obligation, so a request for several can come
     * back having done some of them — reporting the REQUESTED count as
     * released is how a partial batch reads as a clean success.
     */
    releasedLineItemUuids: string[];
  }> {
    actionState.value = 'loading';
    try {
      const res = await settlementApi.refundAndRelease(
        invoiceUuid,
        newKey(),
        lineItemUuids,
        reason,
      );
      await refresh();
      actionState.value = 'success';
      const released = res?.released ?? [];
      return {
        ok: true,
        refundedCents: released.reduce((sum, line) => sum + (line.refundedCents ?? 0), 0),
        // Money still on its way back: an obligation the processor has taken
        // but not confirmed. Only `succeeded` is settled, `void` moved nothing
        // to settle, and `failed` is not on its way anywhere — it needs a
        // human, and the attention feed is where it asks for one.
        pending: released.some((line) => PENDING_OBLIGATIONS.includes(line.obligationStatus)),
        releasedLineItemUuids: released.map((line) => line.lineItemUuid),
      };
    } catch (e) {
      actionState.value = 'error';
      recordActionError(e);
      return { ok: false, refundedCents: 0, pending: false, releasedLineItemUuids: [] };
    }
  }

  // Supervisor-only money-only refund (GT1-586). Returns money and releases
  // nothing, so every line stays on the invoice. It exists for corrections the
  // per-line flow cannot make, such as settling the rest of a refund Neon Pay
  // refused; the server enforces the supervisor role and caps the amount at
  // what the charge has left.
  async function supportRefund(
    invoiceUuid: string,
    amountCents: number,
    reason: string,
  ): Promise<boolean> {
    actionState.value = 'loading';
    try {
      await settlementApi.refund(invoiceUuid, newKey(), amountCents, reason);
      await refresh();
      actionState.value = 'success';
      return true;
    } catch (e) {
      actionState.value = 'error';
      recordActionError(e);
      return false;
    }
  }

  // Reassignment stays one idempotent POST: a client-orchestrated
  // decline-then-offer pair can leave a lot with no owner and no offer.

  // Apply the pre-payment add-ons (donation + fee coverage) on the backend and
  // return the recomputed totals. Reused by the drawer's live preview AND by
  // collect() — the backend gross-up value is the single source of truth. Also
  // patches the matching roster invoice so the list total stays consistent.
  // Throws on a 422 (invoice already paid) so callers can surface "just paid".
  //
  // `method` / `cardBrand` name the rail the fee is priced for. The caller owns
  // them because only the caller knows which rail is selected; omitting them
  // buys the card base rate, which is wrong for every other rail.
  async function applyAddOns(
    invoiceUuid: string,
    opts: {
      donationCents: number;
      coverFees: boolean;
      method?: DcfMethod;
      cardBrand?: DcfCardBrand;
    },
  ): Promise<AddOnTotals> {
    await settlementApi.setDonation(invoiceUuid, opts.donationCents);
    const updated = (await settlementApi.setFeeCoverage(invoiceUuid, {
      enabled: opts.coverFees,
      method: opts.method,
      cardBrand: opts.cardBrand,
    })) as Partial<AddOnTotals>;
    // Fail loudly on a partial/malformed response: a silent `?? 0` would zero the
    // invoice total and let collect() record a $0 offline payment. Throwing here
    // is caught by the drawer, which aborts the charge and refreshes true state.
    const req = (field: keyof AddOnTotals): number => {
      const v = updated[field];
      if (typeof v !== 'number' || !Number.isFinite(v)) {
        throw new Error(`applyAddOns: setFeeCoverage response missing numeric ${field}`);
      }
      return v;
    };
    const totals: AddOnTotals = {
      subtotalCents: req('subtotalCents'),
      donationCents: req('donationCents'),
      feeCoverageCents: req('feeCoverageCents'),
      totalCents: req('totalCents'),
      taxDeductibleCents: req('taxDeductibleCents'),
    };
    const inv = invoices.value.find((i) => i.invoiceUuid === invoiceUuid);
    if (inv) {
      inv.subtotalCents = totals.subtotalCents;
      inv.donationCents = totals.donationCents;
      inv.feeCoverageCents = totals.feeCoverageCents;
      inv.totalCents = totals.totalCents;
      inv.taxDeductibleCents = totals.taxDeductibleCents;
      // Keep the stored decision in step with what we just asked for. Without
      // this the cached invoice still carries the OLD value, and the drawer
      // re-seeds its toggle from that cache on reopen — which is the stale read
      // the 0091 column exists to prevent.
      inv.feeCoverageOptedIn = opts.coverFees;
      // Same reasoning one field further: the amount above is only meaningful
      // alongside the rail it was priced for, so a surface reopening on this
      // cached invoice can tell "this fee is for the rail I have selected" from
      // "this fee is for some other rail and must be recomputed".
      inv.feeCoverageComputedFor = {
        method: opts.method ?? null,
        cardBrand: opts.cardBrand ?? null,
      };
    }
    return totals;
  }

  // Charge-only collect: takes payment by the chosen method, then refreshes true
  // state. Add-ons (donation + fee coverage) are NOT mutated here — the invoice
  // already carries them (baked at generation, or applied via applyAddOns from
  // the drawer). This is why a batch "charge card on file" preserves each
  // invoice's donor-covered fee instead of stripping it. For offline
  // methods pass amountCents = the backend total to record.
  /**
   * What a collect attempt actually did, which is NOT the same question as
   * whether the request succeeded: Neon Pay reports a decline as a 200 carrying
   * a failed transaction, so an exception-only reading calls every declined card
   * a collected payment. `processing` is the ACH/async case — submitted, not yet
   * settled, and not a payment either.
   */
  type CollectOutcome = 'paid' | 'processing' | 'declined' | 'unknown';

  /**
   * Read from the charge's own RESPONSE — the invoice the server wrote in the
   * same request — and never from a later roster refetch.
   *
   * It used to read the refetch, on the reasoning that the refreshed status is
   * what the rest of the drawer agrees with so the toast could not contradict
   * the panel beneath it. The refetch is not reliably readable, and hasn't been
   * since admin charges began publishing an `invoice.*` frame to `admin:{orgId}`:
   * `applyFrame` turns that frame into a second `refresh()`, which bumps
   * `rosterRequestId`, and the refetch this function was waiting on then returns
   * at its superseded guard WITHOUT settling `refreshState`. Every admin charge
   * therefore read as "could not confirm" and toasted "Charge sent — result
   * unconfirmed" over a card that had plainly succeeded or plainly declined,
   * while that very frame repainted the drawer with the truth behind the toast.
   *
   * The response cannot be superseded, needs no round-trip, and is the only
   * thing that answers what THIS charge did rather than what is true now.
   */
  function outcomeFromChargeResult(result: ChargeResult): CollectOutcome {
    // A response we cannot read is the one honest 'unknown': the charge went
    // out and nothing here can say what it did. Guarded explicitly rather than
    // left to throw, so the catch below stays about the request never being
    // answered and this stays about the answer being unreadable.
    //
    // Validated rather than trusted, because the api layer casts (`r.data as
    // ChargeResult`) and nothing else checks. A status this build does not know
    // — the backend shipping a new member first, which a deploy window makes
    // ordinary — is not a decline, and calling it one would tell the operator to
    // retry a charge that may have succeeded. That is the exact reading this
    // whole function exists to stop making.
    const parsed = InvoicePaymentStatusSchema.safeParse(result?.invoice?.paymentStatus);
    if (!parsed.success) return 'unknown';
    switch (invoicePaymentDesignStatus(parsed.data)) {
      case InvoiceStatus.Paid:
        return 'paid';
      case InvoiceStatus.Processing:
        return 'processing';
      // Every remaining KNOWN status means this attempt collected nothing — a
      // refused card leaves the invoice awaiting payment, which is a decline to
      // the operator holding it.
      default:
        return 'declined';
    }
  }

  interface CollectOpts {
    method: 'card_on_file' | 'different_card' | 'ach' | 'cash' | 'check';
    cardToken?: string;
    // Neon Pay token from NeonPayBankAccountField. Kept separate from
    // cardToken so a stale card token can never be charged as ACH (or vice
    // versa) when the operator switches method mid-form.
    achToken?: string;
    // Brand of the card in `cardToken`, as the tokenizer reported it.
    // Only the keyed-card rail forwards it: a fresh token has no stored
    // payment-method row for the server to read the brand off, so without it the
    // authoritative fee recompute prices an Amex at the card base rate. The
    // card-on-file path deliberately drops it — the server reads the stored row,
    // and a client-asserted brand suppresses that lookup.
    cardBrand?: DcfCardBrand;
    checkNumber?: string;
    amountCents?: number;
  }
  async function collect(invoiceUuid: string, opts: CollectOpts): Promise<CollectOutcome> {
    actionState.value = 'loading';
    try {
      let charged: ChargeResult | null = null;
      if (opts.method === 'card_on_file') {
        charged = await settlementApi.chargeOnFile(invoiceUuid, newKey());
      } else if (opts.method === 'different_card') {
        charged = await settlementApi.charge(invoiceUuid, newKey(), opts.cardToken, 'card', opts.cardBrand);
      } else if (opts.method === 'ach') {
        // paymentMethodType 'ach' routes the charge to the org's ACH merchant
        // slot server-side; sending it as 'card' would charge the general
        // merchant and fail at Neon Pay.
        charged = await settlementApi.charge(invoiceUuid, newKey(), opts.achToken, 'ach');
      } else {
        // Offline methods record a payment that already happened in the room.
        // There is no processor to decline it, and the recorded status is
        // whatever the operator just witnessed, so there is no charge response
        // to read and `charged` stays null.
        await settlementApi.recordOffline(invoiceUuid, newKey(), {
          amountCents: opts.amountCents ?? 0,
          method: opts.method as OfflineMethod,
          reference: opts.checkNumber,
        });
      }
      // The outcome is already decided by the response above. This refresh is
      // for the REST of the console — the roster, the totals, the activity feed
      // — so it is awaited for their sake and deliberately not read for ours.
      await refresh();
      const outcome: CollectOutcome = charged === null ? 'paid' : outcomeFromChargeResult(charged);
      actionState.value = outcome === 'declined' ? 'error' : 'success';
      return outcome;
    } catch (err) {
      actionState.value = 'error';
      // An ApiError means the server ANSWERED: it reached us, it names the
      // failure, and nothing was collected beyond what it says. Anything else —
      // a timeout, an abort, a dropped connection — means the request may well
      // have reached Neon Pay and charged the card, and we simply never learned
      // the answer. Telling that operator to "try a different method" is how a
      // bidder gets billed twice, so it is the one case that earns the
      // unconfirmed reading.
      return err instanceof ApiError ? 'declined' : 'unknown';
    }
  }

  async function sendCheckoutEmails(): Promise<boolean> {
    if (!auctionUuid.value) return false;
    actionState.value = 'loading';
    try {
      await settlementApi.sendCheckoutEmails(auctionUuid.value);
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  async function sendReminders(): Promise<boolean> {
    if (!auctionUuid.value) return false;
    actionState.value = 'loading';
    try {
      await settlementApi.sendReminders(auctionUuid.value);
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  async function setReminderSchedule(
    channel: ReminderChannel,
    cadence: ReminderCadence,
  ): Promise<boolean> {
    if (!auctionUuid.value) return false;
    actionState.value = 'loading';
    try {
      reminderSchedule.value = await settlementApi.setReminderSchedule(
        auctionUuid.value,
        channel,
        cadence,
      );
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  async function sendReceipt(invoiceUuid: string): Promise<boolean> {
    actionState.value = 'loading';
    try {
      await settlementApi.sendInvoiceReceipt(invoiceUuid);
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  async function resendCheckoutLink(invoiceUuid: string): Promise<boolean> {
    actionState.value = 'loading';
    try {
      await settlementApi.resendCheckoutLink(invoiceUuid);
      actionState.value = 'success';
      return true;
    } catch {
      actionState.value = 'error';
      return false;
    }
  }

  return {
    auctionUuid,
    invoices,
    eventMoney,
    bidders,
    uninvoicedLots,
    unbidLots,
    outstandingOffers,
    strandedLots,
    refundFailures,
    loadState,
    refreshState,
    actionState,
    lastActionError,
    mode,
    activity,
    logActivity,
    summary,
    workQueue,
    settled,
    bidderRollups,
    applyFrame,
    load,
    loadActivity,
    refresh,
    charge,
    terminals,
    terminalsState,
    hasTerminals,
    terminalsSandbox,
    fetchTerminals,
    persistSelectedTerminal,
    readPersistedTerminal,
    chargeTerminal,
    cancelTerminal,
    recordOffline,
    refundAndRelease,
    supportRefund,
    bidderStackFor,
    eligibleStackFor,
    itemLines,
        lotDetailFor,
    photoFor,
    custodyState,
    custodyFor,
    loadItemCustody,
    targetsState,
    reassignTargetsFor,
    loadReassignTargets,
    reassignmentFor,
    reassignItem,
        submitPlacement,
        loadPlacementTargets,
        placementTargetsFor,
    cancelOffer,
    isUnsold,
    saleCanceledAtFor,
    isSaleCanceled,
    markUnsold,
    withdrawLot,
    applyAddOns,
    collect,
    sendCheckoutEmails,
    sendReminders,
    sendReceipt,
    resendCheckoutLink,
    reminderSchedule,
    setReminderSchedule,
  };
});
